/* ==========================================================================
   Free Gift Tiers — birddogs-style multi-goal cart rewards controller
   Paired with: sections/free-gift-tiers.liquid, assets/free-gift-progress.css

   Architecture (Impulse-native): Impulse rebuilds the cart drawer by scraping an
   alternate Liquid view (/cart?view=ajax) into [data-products] and fires a
   `cart:buildup` event after every rebuild (assets/theme.js → theme.CartForm).
   The <gift-tiers> section is rendered ONCE in layout/theme.liquid (static
   sections support repeatable blocks). On connect we:
     1. relocate the progress bar into #CartDrawer [data-gift-tiers-mount] (above items),
     2. move the gift pools into [data-gift-tiers-cards-mount] (below items),
     3. hide the theme's legacy free-shipping bar / single gift block (no double bars),
     4. recompute ALL reward state from the live cart JSON (theme.cart.getCart) on
        every `cart:buildup` — the single source of truth for what's in the cart.

   Goals are measured on NON-gift items only (a gift can never satisfy its own
   goal → no add/remove loops). We sum `original_line_price` (PRE-discount) so the
   100%-off automatic discount that zeroes a gift can't push the qualifying
   subtotal back below a threshold.

   Make-it-$0 mechanism: a native Shopify AUTOMATIC discount (100% off the gift
   collection). Gift lines carry properties { _free_gift: "true", _reward_goal: "<n>" }.
   We never compute prices — Shopify zeroes the line; we only add/remove.
   ========================================================================== */
(function () {
  'use strict';

  if (customElements.get('gift-tiers')) return;

  var GIFT_PROP = '_free_gift';
  var GOAL_PROP = '_reward_goal';
  var BAR_MOUNT = '[data-gift-tiers-mount]';
  var POOLS_MOUNT = '[data-gift-tiers-cards-mount]';

  function rootUrl() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  function moneyFmt(cents) {
    try {
      if (window.theme && theme.Currency && theme.settings) {
        return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
      }
    } catch (e) {}
    return 'Rs ' + Math.round(cents / 100).toLocaleString();
  }

  function isGiftLine(item) {
    return !!(item && item.properties && String(item.properties[GIFT_PROP]) === 'true');
  }

  function goalOf(item) {
    return String((item && item.properties && item.properties[GOAL_PROP]) || '');
  }

  function fill(str, vars) {
    return String(str || '')
      // Editable customizer templates use {{ name }} placeholders.
      .replace(/\{\{\s*(\w+)\s*\}\}/g, function (m, key) {
        return Object.prototype.hasOwnProperty.call(vars, key) ? vars[key] : m;
      })
      // Locale-derived strings use %NAME% placeholders.
      .replace(/%([A-Z]+)%/g, function (m, key) {
        var lower = key.toLowerCase();
        return Object.prototype.hasOwnProperty.call(vars, lower) ? vars[lower] : m;
      });
  }

  class GiftTiers extends HTMLElement {
    connectedCallback() {
      // Relocate the host (progress bar) into the drawer, once.
      if (!this._relocated) {
        var barMount = document.querySelector(BAR_MOUNT);
        if (barMount && !barMount.contains(this)) {
          // Drop stale instances (e.g. a theme-editor section reload) so we never
          // end up with two bars / two pool blocks in the drawer.
          barMount.querySelectorAll('gift-tiers').forEach(function (old) {
            if (old !== this) old.remove();
          }, this);
          this._relocated = true;
          barMount.appendChild(this); // moves node → connectedCallback fires again
          return;
        }
        this._relocated = true;
      }

      if (this._initialized) return;
      this._initialized = true;
      this.busy = false;
      this.reconciling = false;

      var configEl = this.querySelector('[data-config]');
      try {
        this.config = JSON.parse(configEl.textContent);
      } catch (e) {
        this.config = { goals: [], strings: {} };
      }
      this.goals = (this.config.goals || []).map(function (g) {
        return {
          index: String(g.index),
          measure: g.measure === 'items' ? 'items' : 'amount',
          threshold: Number(g.threshold) || 0,
          rewardType: g.rewardType === 'gift' ? 'gift' : 'shipping',
          label: g.label || '',
          choose: Number(g.choose) || 1
        };
      });
      this.strings = this.config.strings || {};

      if (!this.goals.length) return;

      // Move the pools block below the line items.
      this.poolsEl = this.querySelector('[data-gt-pools]');
      var poolsMount = document.querySelector(POOLS_MOUNT);
      if (this.poolsEl && poolsMount) {
        poolsMount.querySelectorAll('[data-gt-pools]').forEach(function (old) {
          old.remove();
        });
        poolsMount.appendChild(this.poolsEl);
      }

      // Hide the theme's legacy bars so we never show two progress bars.
      this.hideLegacyBars();

      // Build a custom scrollbar under each gift pool's cards row.
      this.initScrollbars();

      this.headlineEl = this.querySelector('[data-headline]');
      this.fillEl = this.querySelector('[data-fill]');
      this.nodeEls = Array.prototype.slice.call(this.querySelectorAll('[data-node]'));

      this.removeAttribute('hidden');
      this.classList.add('gift-tiers--ready');

      this._onBuildup = this.sync.bind(this);
      document.addEventListener('cart:buildup', this._onBuildup);

      this.sync();
    }

    disconnectedCallback() {
      if (this._relocated && document.body.contains(this)) return;
      if (this._onBuildup) document.removeEventListener('cart:buildup', this._onBuildup);
    }

    initScrollbars() {
      if (!this.poolsEl) return;
      this.poolsEl.querySelectorAll('.gift-tiers__cards').forEach(buildCardScrollbar);
    }

    updateScrollbars() {
      if (!this.poolsEl) return;
      this.poolsEl.querySelectorAll('.gift-tiers__cards').forEach(function (cards) {
        if (cards._sbUpdate) cards._sbUpdate();
      });
    }

    hideLegacyBars() {
      [
        '.cart-free-shipping-mount', // native free-shipping bar
        '.free-shipping-wrapper', // legacy discount progress bar
        '.gift__block' // legacy single gift block
      ].forEach(function (sel) {
        document.querySelectorAll(sel).forEach(function (el) {
          el.style.display = 'none';
        });
      });
    }

    /* ----- cart maths (non-gift only) ---------------------------------- */

    nonGiftTotal(cart) {
      return (cart.items || []).reduce(function (sum, it) {
        if (isGiftLine(it)) return sum;
        var price = it.original_line_price != null ? it.original_line_price : it.line_price;
        return sum + price;
      }, 0);
    }

    nonGiftQty(cart) {
      return (cart.items || []).reduce(function (sum, it) {
        return isGiftLine(it) ? sum : sum + it.quantity;
      }, 0);
    }

    isMet(goal, total, qty) {
      return goal.measure === 'amount' ? total >= goal.threshold : qty >= goal.threshold;
    }

    /* ----- sync / reconcile -------------------------------------------- */

    sync() {
      var self = this;
      if (!window.theme || !theme.cart || !theme.cart.getCart) return;
      return theme.cart
        .getCart()
        .then(function (cart) {
          return self.reconcile(cart).then(function (changedCart) {
            self.render(changedCart || cart);
          });
        })
        .catch(function () {})
        .finally(function () {
          self.setBusy(false);
        });
    }

    // Remove every invalid gift in ONE /cart/update.js call (instant rewind).
    // Safe in a single pass: goals exclude gifts, so removing gifts never changes
    // goal state. Returns a fresh cart if anything changed, else null.
    reconcile(cart) {
      var self = this;
      if (this.reconciling) return Promise.resolve(null);

      var total = this.nonGiftTotal(cart);
      var qty = this.nonGiftQty(cart);
      var byIndex = {};
      this.goals.forEach(function (g) { byIndex[g.index] = g; });

      var perGoal = {};
      var removeKeys = [];

      (cart.items || []).forEach(function (item) {
        if (!isGiftLine(item)) return;
        var gi = goalOf(item);
        var goal = byIndex[gi];
        var remove = false;

        if (!goal || goal.rewardType !== 'gift') {
          remove = true;
        } else if (!self.isMet(goal, total, qty)) {
          remove = true;
        } else {
          perGoal[gi] = (perGoal[gi] || 0) + item.quantity;
          if (perGoal[gi] > goal.choose) remove = true;
        }
        if (remove) removeKeys.push(item.key);
      });

      if (!removeKeys.length) return Promise.resolve(null);

      this.reconciling = true;
      return this.removeLines(removeKeys)
        .then(function (newCart) {
          self.triggerRebuild(); // let Impulse rebuild the line-item markup too
          return newCart;
        })
        .catch(function () { return null; })
        .finally(function () { self.reconciling = false; });
    }

    /* ----- render ------------------------------------------------------ */

    render(cart) {
      var self = this;
      // A cart change re-renders the drawer; drop any open (now stale-positioned) popover.
      closeAllVariantPopovers();
      var total = this.nonGiftTotal(cart);
      var qty = this.nonGiftQty(cart);
      var N = this.goals.length;

      // --- fill ---
      // Nodes render at the RIGHT edge of each 1/N cell (CSS align-items:end),
      // so goal i (0-indexed) sits at ((i+1)/N)*100%. The old formula used the
      // segment CENTRE ((2i-1)/2N) — a single goal's node landed at 50% and
      // capped the bar at half, so Rs.3,901 / 5,000 only filled ~39%.
      var filled = 0;
      var segmentDone = false;
      var allMet = true;

      this.goals.forEach(function (goal, i) {
        var pos = ((i + 1) / N) * 100;   // this node (right edge of its cell)
        var prevPos = (i / N) * 100;     // previous node (start of this segment)
        var met = self.isMet(goal, total, qty);
        if (!met) allMet = false;
        if (segmentDone) return;

        if (met) {
          filled = pos;
        } else {
          // Progress WITHIN this segment. Measure from the previous goal's
          // threshold when it shares the same unit (so the band is meaningful),
          // otherwise from zero — keeps mixed amount/quantity goals sane.
          var current = goal.measure === 'amount' ? total : qty;
          var floor = 0;
          if (i > 0 && self.goals[i - 1].measure === goal.measure) {
            floor = self.goals[i - 1].threshold;
          }
          var span = goal.threshold - floor;
          var ratio = span > 0 ? (current - floor) / span : 0;
          if (ratio < 0) ratio = 0;
          if (ratio > 1) ratio = 1;
          filled = prevPos + ratio * (pos - prevPos);
          segmentDone = true;
        }
      });

      if (allMet) filled = 100 + 10;
      if (this.fillEl) this.fillEl.style.width = Math.round(filled - 10) + '%';

      // --- nodes (keep icon, toggle green check badge) ---
      this.nodeEls.forEach(function (node) {
        var goal = self.goals[Number(node.dataset.goalIndex) - 1];
        if (!goal) return;
        node.classList.toggle('is-met', self.isMet(goal, total, qty));
      });

      // --- headline (single first-unmet line) ---
      this.classList.toggle('gift-tiers--complete', allMet);
      if (this.headlineEl) {
        var firstUnmet = null;
        for (var i = 0; i < this.goals.length; i++) {
          if (!this.isMet(this.goals[i], total, qty)) { firstUnmet = this.goals[i]; break; }
        }
        // "goal" basis shows the full threshold (Shop Rs.10,000); "remaining" shows
        // how much more to add (Spend Rs.4,000 more). {{ gifts }} = gifts at this goal.
        var useGoalTotal = this.strings.amountBasis === 'goal';
        if (!firstUnmet) {
          this.headlineEl.textContent = this.strings.allUnlocked || "You've unlocked all rewards!";
        } else {
          var gifts = firstUnmet.rewardType === 'gift' ? firstUnmet.choose : '';
          var giftsHtml = '<strong class="gift-tiers__amount">' + gifts + '</strong>';
          var tplAmount = this.strings.headlineAmount || 'Spend {{ amount }} to unlock {{ reward }}';
          var tplQty = this.strings.headlineQuantity || 'Add {{ count }} more to unlock {{ reward }}';
          if (firstUnmet.measure === 'amount') {
            var amountVal = useGoalTotal ? firstUnmet.threshold : Math.max(0, firstUnmet.threshold - total);
            this.headlineEl.innerHTML = fill(tplAmount, {
              amount: '<strong class="gift-tiers__amount">' + moneyFmt(firstUnmet.threshold) + '</strong>',
              reward: escapeHtml(firstUnmet.label),
              gifts: giftsHtml
            });
          } else {
            var qtyVal = useGoalTotal ? firstUnmet.threshold : Math.max(0, firstUnmet.threshold - qty);
            this.headlineEl.innerHTML = fill(tplQty, {
              count: '<strong class="gift-tiers__amount">' + qtyVal + '</strong>',
              reward: escapeHtml(firstUnmet.label),
              gifts: giftsHtml
            });
          }
        }
      }

      // --- pools ---
      this.renderPools(cart, total, qty);
      this.updateScrollbars();

      // --- optional: exclude gifts from the cart-icon badge ---
      if (this.dataset.excludeBadge === 'true') {
        var giftQty = (cart.items || []).reduce(function (n, it) { return isGiftLine(it) ? n + it.quantity : n; }, 0);
        var shown = Math.max(0, (cart.item_count || 0) - giftQty);
        document.querySelectorAll('.cart-link__bubble-num').forEach(function (el) { el.innerText = shown; });
      }
    }

    renderPools(cart, total, qty) {
      var self = this;
      if (!this.poolsEl) return;

      // Selected gift line-key per (goal + product), and per-goal chosen counts.
      var selected = {}; // "goal:productId" -> lineKey
      var chosen = {}; // goal -> count
      (cart.items || []).forEach(function (it) {
        if (!isGiftLine(it)) return;
        var gi = goalOf(it);
        chosen[gi] = (chosen[gi] || 0) + it.quantity;
        selected[gi + ':' + it.product_id] = it.key;
      });

      this.poolsEl.querySelectorAll('[data-pool]').forEach(function (pool) {
        var gi = String(pool.dataset.goalIndex);
        var goal = self.goals[Number(gi) - 1];
        if (!goal) return;
        var choose = Number(pool.dataset.choose) || 1;
        var met = self.isMet(goal, total, qty);
        var count = chosen[gi] || 0;
        var full = count >= choose;

        pool.classList.toggle('is-locked', !met);
        pool.classList.toggle('is-unlocked', met);
        pool.classList.toggle('is-full', full);

        var tag = pool.querySelector('[data-unlocked-tag]');
        if (tag) tag.hidden = !met;

        var countEl = pool.querySelector('[data-pool-count]');
        if (countEl) {
          if (met) {
            countEl.textContent = full
              ? fill(self.strings.selectedCount, { count: count })
              : fill(self.strings.chooseCount, { count: choose });
          } else if (goal.measure === 'amount') {
            countEl.innerHTML = fill(self.strings.poolUnlockAmount, {
              amount: '<strong class="gift-tiers__amount">' + moneyFmt(Math.max(0, goal.threshold - total)) + '</strong>'
            });
          } else {
            countEl.textContent = fill(self.strings.poolUnlockQuantity, { count: Math.max(0, goal.threshold - qty) });
          }
        }

        pool.querySelectorAll('[data-card]').forEach(function (card) {
          var key = gi + ':' + card.dataset.productId;
          var lineKey = selected[key];
          var isSelected = !!lineKey;
          card.classList.toggle('is-selected', isSelected);
          card.classList.toggle('is-disabled', met && !isSelected && full);
          if (isSelected) {
            card.dataset.lineKey = lineKey;
          } else {
            delete card.dataset.lineKey;
          }
          var btn = card.querySelector('.gift-tiers__card-btn');
          if (btn) {
            btn.setAttribute('aria-label', isSelected ? (self.dataset.removeLabel || 'Remove') : (self.dataset.addLabel || 'Add'));
          }
        });
      });
    }

    /* ----- cart writes ------------------------------------------------- */

    addGift(variantId, goalIndex, card) {
      var self = this;
      if (!variantId) return;
      this.setBusy(true);
      if (card) card.classList.add('is-loading');
      fetch(rootUrl() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: Number(variantId),
          quantity: 1,
          properties: { _free_gift: 'true', _reward_goal: String(goalIndex) }
        })
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data && data.status) throw new Error(data.description || 'add failed');
          self.triggerRebuild(); // → cart:buildup → sync() → reconcile()
        })
        .catch(function () {
          if (card) card.classList.remove('is-loading');
          self.setBusy(false);
        });
    }

    removeGift(card) {
      var self = this;
      var key = card && card.dataset.lineKey;
      if (!key) { this.sync(); return; }
      this.setBusy(true);
      card.classList.add('is-loading');
      this.removeLines([key])
        .then(function () { self.triggerRebuild(); })
        .catch(function () {
          card.classList.remove('is-loading');
          self.setBusy(false);
        });
    }

    // One request, one re-render — used for both single removes and batched rewind.
    removeLines(keys) {
      var updates = {};
      keys.forEach(function (k) { updates[k] = 0; });
      return fetch(rootUrl() + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: updates })
      }).then(function (r) { return r.json(); });
    }

    setBusy(state) {
      this.busy = state;
      this.classList.toggle('is-busy', state);
    }

    // Ask Impulse to rebuild the drawer markup. theme.CartForm listens to
    // `cart:build` and fires `cart:buildup` when done → our sync() runs.
    triggerRebuild() {
      document.dispatchEvent(new CustomEvent('cart:build'));
    }
  }

  function escapeHtml(str) {
    return String(str || '').replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Build a fully custom horizontal scrollbar under a cards row. The native bar is
  // hidden in CSS (no OS arrows/track); this gives identical styling on every browser.
  function buildCardScrollbar(cards) {
    if (cards._sbInit) return;
    cards._sbInit = true;

    var bar = document.createElement('div');
    bar.className = 'gift-tiers__scrollbar is-hidden';
    var thumb = document.createElement('div');
    thumb.className = 'gift-tiers__scrollbar-thumb';
    bar.appendChild(thumb);
    cards.insertAdjacentElement('afterend', bar);

    function update() {
      var sw = cards.scrollWidth;
      var cw = cards.clientWidth;
      var scrollable = !!cw && sw > cw + 1;
      cards.classList.toggle('is-draggable', scrollable); // grab cursor only when pan-able
      if (!scrollable) { bar.classList.add('is-hidden'); return; }
      bar.classList.remove('is-hidden');
      var trackW = bar.clientWidth;
      var thumbW = Math.max(24, Math.round((cw / sw) * trackW));
      thumb.style.width = thumbW + 'px';
      var maxScroll = sw - cw;
      var maxThumb = trackW - thumbW;
      var x = maxScroll > 0 ? (cards.scrollLeft / maxScroll) * maxThumb : 0;
      thumb.style.transform = 'translateX(' + x + 'px)';
    }
    cards._sbUpdate = update;

    cards.addEventListener('scroll', update, { passive: true });

    // Drag the thumb.
    var dragging = false, startX = 0, startScroll = 0;
    thumb.addEventListener('pointerdown', function (e) {
      dragging = true;
      startX = e.clientX;
      startScroll = cards.scrollLeft;
      bar.classList.add('is-dragging');
      try { thumb.setPointerCapture(e.pointerId); } catch (_) {}
      e.preventDefault();
      e.stopPropagation();
    });
    thumb.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var maxThumb = bar.clientWidth - thumb.offsetWidth;
      var maxScroll = cards.scrollWidth - cards.clientWidth;
      var dx = e.clientX - startX;
      cards.scrollLeft = startScroll + (maxThumb > 0 ? (dx / maxThumb) * maxScroll : 0);
    });
    function endDrag(e) {
      if (!dragging) return;
      dragging = false;
      bar.classList.remove('is-dragging');
      try { thumb.releasePointerCapture(e.pointerId); } catch (_) {}
    }
    thumb.addEventListener('pointerup', endDrag);
    thumb.addEventListener('pointercancel', endDrag);

    // Click on the track jumps the row to that position.
    bar.addEventListener('pointerdown', function (e) {
      if (e.target === thumb) return;
      var rect = bar.getBoundingClientRect();
      var maxThumb = bar.clientWidth - thumb.offsetWidth;
      var maxScroll = cards.scrollWidth - cards.clientWidth;
      var pos = e.clientX - rect.left - thumb.offsetWidth / 2;
      var ratio = maxThumb > 0 ? Math.min(1, Math.max(0, pos / maxThumb)) : 0;
      cards.scrollLeft = ratio * maxScroll;
    });

    // Desktop: click-drag anywhere on the products row to pan it (touch already pans
    // natively). A small movement threshold keeps the +/- buttons clickable; the
    // trailing click is swallowed after a real drag so it can't add/remove a gift.
    (function enableDragToScroll() {
      var down = false, moved = false, startX = 0, startScroll = 0, pid = null;
      var THRESHOLD = 6;

      cards.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'touch') return;
        if (e.button != null && e.button !== 0) return;
        down = true; moved = false; startX = e.clientX; startScroll = cards.scrollLeft;
        pid = e.pointerId; cards._suppressClick = false;
      });

      cards.addEventListener('pointermove', function (e) {
        if (!down) return;
        var dx = e.clientX - startX;
        if (!moved && Math.abs(dx) < THRESHOLD) return;
        if (!moved) {
          moved = true;
          cards.classList.add('is-grabbing');
          try { cards.setPointerCapture(pid); } catch (_) {}
        }
        cards.scrollLeft = startScroll - dx;
        e.preventDefault();
      });

      function end() {
        if (!down) return;
        down = false;
        try { cards.releasePointerCapture(pid); } catch (_) {}
        if (moved) { cards.classList.remove('is-grabbing'); cards._suppressClick = true; }
      }
      cards.addEventListener('pointerup', end);
      cards.addEventListener('pointercancel', end);
      cards.addEventListener('dragstart', function (e) { e.preventDefault(); });

      // Capture phase: cancel the post-drag click before it reaches the gift handler.
      cards.addEventListener('click', function (e) {
        if (cards._suppressClick) { e.preventDefault(); e.stopPropagation(); cards._suppressClick = false; }
      }, true);
    })();

    update();
    // Lazy images change scrollWidth after load — recompute a couple of times.
    setTimeout(update, 300);
    setTimeout(update, 1000);
  }

  // Close every open variant popover and return it to its home card so the markup
  // stays tidy (the popovers are temporarily moved to the pool root while open).
  function closeAllVariantPopovers(except) {
    document.querySelectorAll('.gift-tiers__variants').forEach(function (pop) {
      if (pop === except) return;
      pop.hidden = true;
      pop.style.position = '';
      pop.style.top = '';
      pop.style.left = '';
      pop.style.visibility = '';
      var home = pop._homeCard;
      if (home) {
        var media = home.querySelector('.gift-tiers__card-media');
        if (media && pop.parentElement !== media) media.appendChild(pop);
        var toggle = home.querySelector('[data-gift-variant-toggle]');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      }
    });
  }

  // The cards row is a horizontal scroller (overflow clips anything inside it), so a
  // popover rendered inside a card would be invisible. We move the popover to the pool
  // root (which doesn't clip) and position it absolutely under the trigger button.
  function openVariantPopover(card, btn) {
    var pop = card._giftPopover || card.querySelector('.gift-tiers__variants');
    if (!pop) return;
    card._giftPopover = pop;
    pop._homeCard = card;

    var pools = card.closest('[data-gt-pools]') || document.body;
    if (getComputedStyle(pools).position === 'static') pools.style.position = 'relative';
    pools.appendChild(pop);

    pop.hidden = false;
    pop.style.position = 'absolute';
    pop.style.visibility = 'hidden';
    pop.style.top = '0px';
    pop.style.left = '0px';

    var poolsRect = pools.getBoundingClientRect();
    var btnRect = btn.getBoundingClientRect();
    var cardRect = card.getBoundingClientRect();
    var pw = pop.offsetWidth;

    // Align the popover's right edge under the button; open below the card.
    var left = btnRect.right - poolsRect.left - pw;
    var top = cardRect.bottom - poolsRect.top + 8;
    var maxLeft = pools.clientWidth - pw;
    if (left > maxLeft) left = maxLeft;
    if (left < 0) left = 0;

    pop.style.left = left + 'px';
    pop.style.top = top + 'px';
    pop.style.visibility = '';
    btn.setAttribute('aria-expanded', 'true');
  }

  function activeTiers() {
    return document.querySelector('gift-tiers.gift-tiers--ready');
  }

  // Delegated clicks survive section re-renders and work for the relocated pools.
  document.addEventListener('click', function (event) {
    var tiers = activeTiers();

    // Variant chosen inside a popover.
    var variantBtn = event.target.closest('.gift-tiers__variant[data-gift-add]');
    if (variantBtn) {
      event.preventDefault();
      if (variantBtn.hasAttribute('disabled') || !tiers || tiers.busy) return;
      var vCard = variantBtn.closest('[data-card]');
      tiers.addGift(variantBtn.dataset.variantId, variantBtn.dataset.rewardGoal, vCard);
      closeAllVariantPopovers();
      return;
    }

    // The main +/- button on a card.
    var cardBtn = event.target.closest('.gift-tiers__card-btn');
    if (cardBtn) {
      event.preventDefault();
      var card = cardBtn.closest('[data-card]');
      if (!card || !tiers || tiers.busy) return;
      if (card.classList.contains('is-disabled') || card.closest('.is-locked')) return;

      if (card.classList.contains('is-selected')) {
        tiers.removeGift(card);
      } else if (cardBtn.dataset.variantId) {
        tiers.addGift(cardBtn.dataset.variantId, cardBtn.dataset.rewardGoal, card);
      } else {
        // Multi-variant: open the "choose your option" popover.
        var popover = card._giftPopover || card.querySelector('.gift-tiers__variants');
        if (popover) {
          var willOpen = popover.hidden;
          closeAllVariantPopovers();
          if (willOpen) openVariantPopover(card, cardBtn);
        }
      }
      return;
    }

    if (event.target.closest('[data-gift-variant-close]')) {
      event.preventDefault();
      closeAllVariantPopovers();
      return;
    }

    if (!event.target.closest('.gift-tiers__variants')) {
      closeAllVariantPopovers();
    }
  });

  // The popover is anchored to the card's position, so close it when the cards row
  // scrolls horizontally or the viewport resizes (it would otherwise drift).
  document.addEventListener('scroll', function (event) {
    var t = event.target;
    if (t && t.closest && t.closest('.gift-tiers__cards')) closeAllVariantPopovers();
  }, true);
  window.addEventListener('resize', function () { closeAllVariantPopovers(); });

  customElements.define('gift-tiers', GiftTiers);

  // Re-sync when the cart drawer opens (returning customer with a gift already in
  // the cart, or any change while the drawer was closed). Impulse fires a
  // drawer-scoped event; the cart drawer id is "CartDrawer".
  function syncOnOpen() {
    var el = activeTiers();
    if (el && el.sync) el.sync();
  }
  document.addEventListener('drawerOpen.CartDrawer', syncOnOpen);
})();
